import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserLoginMutation, useLazyGetUserInfoQuery, useCreateUserMutation } from '@features/user';
import { Box, CircularProgress } from '@mui/material';
import SentryService from '../services/SentryService';
import { UTMTracker } from '@services/utm';
import { FRONTEND_APP_VERSION } from '../constants';
import { Auth0Initializer } from '../components/Auth0Initializer';
import { setLastUserLogin, type LastUserLogin } from '../types/auth';
import { AccountBlockedError } from '../components/errors/AccountBlockedError';

export type UserDataType = any;

export const AuthenticationCallbackWrapper = () => {
  // production
  const domain = 'smartscribehealth.us.auth0.com';
  const clientId = 'RG4FwiozZWwAAe83xQxv2metpgJaPe0j';
  const redirectUri = window.location.origin + '/callback';
  const audience = 'https://api.smartscribe.health';

  // development
  // const domain = "smartscribe-development.us.auth0.com";
  // const clientId = "ohWExKeDUG8L1LY5oRJXPseSfaqN1mid";
  // const redirectUri = window.location.origin + "/callback";
  // const audience = "https://api.smartscribe.health/development";

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience: audience,
        redirect_uri: redirectUri,
        scope: 'openid profile email offline_access',
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <Auth0Initializer>
        <AuthenticationCallback />
      </Auth0Initializer>
    </Auth0Provider>
  );
};

const AuthenticationCallback = () => {
  const { error, getAccessTokenSilently, user } = useAuth0();
  const [userLogin] = useUserLoginMutation();
  const [getUserInfo] = useLazyGetUserInfoQuery();
  const [createUser] = useCreateUserMutation();
  const navigate = useNavigate();
  const loginInProgressRef = useRef(false);
  const callbackInitRef = useRef(false);

  // Function to append parameters from localStorage to a URL
  const appendParamsFromLocalStorage = (url: string) => {
    const paramsJson = localStorage.getItem('urlParams');
    if (!paramsJson) return url;

    const paramsObj = JSON.parse(paramsJson);
    if (Object.keys(paramsObj).length === 0) return url;

    const urlObj = new URL(url, window.location.origin);

    Object.keys(paramsObj).forEach(key => {
      urlObj.searchParams.set(key, paramsObj[key]);
    });

    localStorage.removeItem('urlParams');

    return urlObj.toString();
  };

  const saveAccessToken = async () => {
    try {
      console.log('Attempting to get access token...');
      const accessToken = await getAccessTokenSilently();
      console.log('Got access token:', !!accessToken);
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
      }
    } catch (err) {
      console.error('Token error:', err);
    }
  };

  const updateUserLogins = async () => {
    // Use ref for atomic check-and-set to prevent race conditions
    if (loginInProgressRef.current) {
      console.log('Login already in progress, skipping...');
      return;
    }

    // Check session storage as secondary protection against double-processing
    const callbackLoginAttempt = sessionStorage.getItem('auth_callback_login_attempted');
    if (callbackLoginAttempt === 'true') {
      // Get returnTo from localStorage, or fall back to sessionStorage backup
      const returnTo = localStorage.getItem('returnTo') || sessionStorage.getItem('auth_callback_destination');
      if (returnTo && returnTo !== '/') {
        // We have a destination - navigate to it
        const urlWithParams = appendParamsFromLocalStorage(returnTo);
        const url = new URL(urlWithParams, window.location.origin);
        const path = url.pathname + url.search + url.hash;
        localStorage.removeItem('returnTo');
        navigate(path);
      } else {
        // No destination saved - just go to dashboard
        navigate('/');
      }
      return;
    }

    // Atomically set the flag
    loginInProgressRef.current = true;
    sessionStorage.setItem('auth_callback_login_attempted', 'true');

    try {
      const result = await userLogin({
        device_info: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          platform: 'web',
          userAgent: navigator.userAgent,
          appVersion: FRONTEND_APP_VERSION,
          hostname: window.location.hostname,
          screen: { height: screen.height, width: screen.width },
        } as any, // Type assertion because the type definition is too restrictive
      }).unwrap();

      // Since the API doesn't return a status in the response type,
      // we assume success if no error was thrown
      const returnTo = localStorage.getItem('returnTo') || '/';
      const urlWithParams = appendParamsFromLocalStorage(returnTo);

      // Parse the URL to extract the path, search params, and hash
      const url = new URL(urlWithParams, window.location.origin);
      const path = url.pathname + url.search + url.hash;

      // Save destination to sessionStorage as backup in case callback runs again
      sessionStorage.setItem('auth_callback_destination', path);

      // Clear the returnTo from localStorage
      localStorage.removeItem('returnTo');

      // DON'T clear the session flag here - keep it to prevent double navigation
      // if something causes callback to load again. It will naturally clear when
      // the browser session ends.

      // Navigate using React Router
      navigate(path);
    } catch (error) {
      console.error('User login failed:', error);
      // Don't clear the flags on error to prevent retries
    } finally {
      // Keep the ref set to prevent retries in the same component instance
      // Session storage will handle cross-instance protection
    }
  };

  const attemptCreateUser = async () => {
    if (!user?.email) return;
    try {
      const inviteToken = localStorage.getItem('invite_token') || undefined;
      const utm = UTMTracker.getUTMsForAPI();
      await createUser({
        createUserRequest: {
          email: user.email,
          invite_token: inviteToken,
          platform: 'web',
          utm: Object.keys(utm).length > 0 ? utm : undefined,
        },
      }).unwrap();
    } catch (error: any) {
      const status = error?.status || error?.data?.status;
      if (status === 409) return; // User exists — normal login, not an error
      console.error('Atomic user creation failed:', error);
      SentryService.captureException(error, {
        level: 'warning',
        tags: { component: 'AuthenticationCallback', action: 'createUser' },
      });
      // Non-blocking: old flow via updateUserInfo will still work as fallback
    }
  };

  const saveUserInfo = async () => {
    try {
      const { data } = await getUserInfo(undefined);
      if (data) {
        // User info is now stored in RTK Query cache automatically
        // Auth0 picture is stored in Redux via Auth0Initializer

        // Save user info for smart login on next visit
        if (user?.email) {
          const lastUserLogin: LastUserLogin = {
            email: user.email,
            name: user.name,
            picture: user.picture,
            connection: user.sub?.split('|')[0] || 'auth0',
          };
          setLastUserLogin(lastUserLogin);
        }

        // call redirect after user info fetched
        updateUserLogins();
      }
    } catch (error) {
      console.error('Failed to get user info:', error);
    }
  };

  useEffect(() => {
    if (!user) return;
    // Ref guard prevents duplicate calls on re-renders
    if (callbackInitRef.current) return;
    callbackInitRef.current = true;

    saveAccessToken();
    attemptCreateUser().then(() => saveUserInfo());
  }, [user]);

  // Add a timeout to refresh the page if the redirect doesn't happen within 10 seconds
  // Skip if there's an error - no point in refreshing when showing error page
  useEffect(() => {
    if (error) {
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    // Get current refresh count from sessionStorage or initialize to 0
    const refreshCount = parseInt(sessionStorage.getItem('auth_refresh_count') || '0');

    if (refreshCount < 3) {
      timeoutId = setTimeout(() => {
        console.log(
          `Timeout reached: Refreshing the page (attempt ${
            refreshCount + 1
          } of 3) as redirect didn't happen`,
        );
        // Increment the refresh count
        sessionStorage.setItem('auth_refresh_count', (refreshCount + 1).toString());
        window.location.reload();
      }, 10000); // 10 seconds timeout
    } else {
      console.log('Maximum refresh attempts (3) reached. Not refreshing anymore.');
    }

    // Cleanup function that always runs
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Clean up session storage when component unmounts after max attempts
      if (refreshCount >= 3) {
        sessionStorage.removeItem('auth_refresh_count');
        sessionStorage.removeItem('auth_callback_login_attempted');
      }
    };
  }, [error]);

  // Handle error case after all hooks
  if (error) {
    // Check if this is a blocked user error from Auth0
    // Auth0 returns: error=unauthorized&error_description=user%20is%20blocked
    const errorMessage = error.message?.toLowerCase() || '';
    const isBlockedUser =
      errorMessage.includes('blocked') ||
      errorMessage.includes('user is blocked');

    if (isBlockedUser) {
      return <AccountBlockedError />;
    }

    return <div className="max-w-screen-lg mx-auto">{error.message}</div>;
  }

  return (
    <Box sx={{ display: 'flex', flex: 1, height: '100vh', width: '100vw' }}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <CircularProgress />
        {parseInt(sessionStorage.getItem('auth_refresh_count') || '0') >= 3 && (
          <Box sx={{ mt: 3, textAlign: 'center', maxWidth: '80%' }}>
            <p>
              Authentication is taking longer than expected. Please try refreshing the page manually
              or contact support if the issue persists.
            </p>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AuthenticationCallback;
